import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig, education } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;

      &:after {
        top: 15px;
        left: 15px;
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 20px;
      left: 20px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hey there! I’m Sandeep — a software engineer who’s been obsessed with figuring
              out how things work since I was 12, when breaking and rebuilding games was my
              favorite pastime. That curiosity eventually grew into a love for designing scalable
              systems, debugging the bizarre, and making tech just a little more dependable.
            </p>

            <p>
              These days, I build backend systems at{' '}
              <a href="https://www.nykaa.com/" target="_blank" rel="noreferrer">
                Nykaa
              </a>, where I’m helping unify authentication and move
              large-scale jobs to event-driven architectures. Before that, I was at{' '}
              <a href="https://www.jungleegames.com/" target="_blank" rel="noreferrer">
                Junglee Games
              </a>, integrating shared wallets and KYC systems across gaming titles.
            </p>

            <p>
              When I’m not writing code, I’m usually self-hosting something new, automating
              the boring stuff, or diving into side projects that scratch the itch to learn
              (and occasionally break) something cool. 
            </p>

            <p>Here’s my educational background:</p>
          </div>

          <ul className="skills-list">
            {education.map((degree, i) => (
              <li key={i}>
                ({degree.year}) {degree.name}{' '}
                <a href={degree.link} target="_blank" rel="noreferrer">
                  ({degree.institute})
                </a> -{' '}
                {degree.gpaLink === '' ? degree.score :
                  <a href={degree.gpaLink} target="_blank" rel="noreferrer">
                    {degree.score}
                  </a>
                }
              </li>
            ))}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
